import React, { Component } from 'react';
import '../styles/StatViews.css';
import { getStatViews, PAGE_SIZES, DEFAULT_PAGE_SIZE, PAID_RIGHTS } from '../services/statViewsService';
import {
  Alert,
  Box,
  Paper,
  Stack,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  InputLabel,
  Button,
  Chip,
  Switch,
  TextField,
  Tooltip
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';

// ── Helpers purs (exportés pour `src/tests/statViews.test.js`) ─────────────────────────────

// Le mois par défaut de l'API est le mois COURANT. L'écran doit l'afficher dans ses selects :
// laissés vides, l'utilisateur ne savait pas quel mois il regardait (et le nombre de pages
// change avec le mois — 30 en août 2026, 23 en septembre).
export const currentPeriod = (now = new Date()) => ({
  month: String(now.getMonth() + 1),
  year: String(now.getFullYear())
});

export const FIRST_YEAR = 2015;
export const yearOptions = (now = new Date()) => {
  const last = now.getFullYear() + 1;
  const years = [];
  for (let y = FIRST_YEAR; y <= last; y++) years.push(String(y));
  return years;
};

export const pageCountFor = (total, pageSize) =>
  Math.max(1, Math.ceil((parseInt(total, 10) || 0) / (parseInt(pageSize, 10) || DEFAULT_PAGE_SIZE)));

// Paramètres d'appel dérivés de l'état — TOUS les filtres (mois, année, plan, recherche, taille)
// sont relus à chaque page : changer de page ne remet jamais le mois à zéro.
export const requestFor = (state, page) => [
  page,
  state.month || false,
  state.year || false,
  {
    pageSize: state.pageSize || DEFAULT_PAGE_SIZE,
    rights: state.paidOnly ? PAID_RIGHTS : '',
    query: (state.query || '').trim()
  }
];

// L'API renvoyait `[]` (tableau nu) sur une page vide avant le fix côté serveur ; tant que
// l'API corrigée n'est pas déployée, une page vide doit rester une page vide, pas un crash.
export const NO_FILTERS_ECHO = { rights: null, query: null };

export const normalizeResponse = (result, requestedPage) => {
  if (!result || Array.isArray(result) || typeof result !== 'object') {
    return { hits: [], page: requestedPage, total_elem: 0, comments: 0, loads: 0, filters: NO_FILTERS_ECHO };
  }
  return {
    hits: Array.isArray(result.views) ? result.views : [],
    page: parseInt(result.page_nbr, 10) || requestedPage,
    total_elem: parseInt(result.total_elem, 10) || 0,
    comments: parseInt(result.comments || 0, 10),
    loads: parseInt(result.loads || 0, 10),
    // Écho des filtres normalisés appliqués par l'API (absent = API antérieure au correctif)
    filters: {
      rights: typeof result.rights === 'string' ? result.rights : null,
      query: typeof result.query === 'string' ? result.query : null
    }
  };
};

// Une API antérieure au correctif ignore `rights`/`query` SANS le dire : elle ne les écho pas.
// L'écran doit alors prévenir plutôt qu'afficher « tous les sites » sous un switch coché.
// (L'écho est aussi le marqueur discriminant du déploiement DEV, règle #4.)
export const filtersIgnored = (requested, filters) =>
  Boolean(
    (requested && requested.rights && (!filters || filters.rights === null)) ||
    (requested && requested.query && (!filters || filters.query === null))
  );

// `data-state` du conteneur : ancre des recettes `gc-recette-ui` (un compteur rendu avant la
// réponse n'est pas un oracle — CLAUDE.md super-admin).
export const screenState = ({ isLoading, error, hits }) => {
  if (error) return 'error';
  if (isLoading) return 'loading';
  return hits && hits.length > 0 ? 'ready' : 'empty';
};

class StatViews extends Component {
  constructor(props) {
    super(props);
    const period = currentPeriod();
    this.state = {
      hits: [],
      page: 1,
      total_elem: 0,
      comments: 0,
      loads: 0,
      isLoading: false,
      error: null,
      month: period.month,
      year: period.year,
      pageSize: DEFAULT_PAGE_SIZE,
      paidOnly: false,
      query: '',
      filtersIgnored: false
    };
    this.fetchPage = this.fetchPage.bind(this);
    this.onFilterSubmit = this.onFilterSubmit.bind(this);
    this.onPageChange = this.onPageChange.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    this.fetchPage(1);
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  fetchPage(page) {
    this.setState({ isLoading: true, error: null });
    // Filtres figés AVANT l'appel : c'est CETTE demande qu'on compare à l'écho de la réponse
    const request = requestFor(this.state, page);
    getStatViews(...request)
      .then((result) => {
        if (!this._isMounted) return;
        // `HTTP 200 + { gcCode: 410 }` = session expirée, pas une donnée (quirk `ensureAuthenticated`).
        if (result && !Array.isArray(result) && result.gcCode) {
          this.setState({ isLoading: false, error: 'session_expired', hits: [] });
          return;
        }
        const normalized = normalizeResponse(result, page);
        this.setState({
          ...normalized,
          filtersIgnored: filtersIgnored(request[3], normalized.filters),
          isLoading: false
        });
      })
      .catch((err) => {
        if (!this._isMounted) return;
        this.setState({ isLoading: false, error: (err && err.message) || 'error' });
      });
  }

  onFilterSubmit(e) {
    e.preventDefault();
    this.fetchPage(1);
  }

  onPageChange(e, value) {
    this.fetchPage(value);
  }

  getPlanChipProps(ws) {
    const rights = (ws?.rights || '').toLowerCase();
    const planName = (ws?.plan?.name || '').toLowerCase();
    const maxViews = Number(ws?.plan?.meta?.max_views);
    const baseLabel = ws?.plan?.name || rights || '—';

    // Enterprise: violet
    if (rights === 'enterprise' || planName.includes('enterprise')) {
      return { label: baseLabel || 'enterprise', color: undefined, sx: { bgcolor: '#8B5CF6', color: '#FFFFFF' } };
    }

    // Overrides par max_views
    if (maxViews === 1500000) {
      return { label: baseLabel, color: undefined, sx: { bgcolor: '#FEF08A', color: '#111827' } }; // jaune clair
    }
    if (maxViews === 5000000) {
      return { label: baseLabel, color: undefined, sx: { bgcolor: '#FB923C', color: '#111827' } }; // orange
    }

    // Mapping standard
    if (rights === 'free' || planName === 'free') {
      return { label: 'free', color: 'info' }; // bleu
    }
    if (rights === 'expert' || planName.startsWith('expert')) {
      return { label: ws?.plan?.name || 'expert', color: 'warning' }; // jaune
    }
    if (rights === 'pro' || planName.startsWith('pro')) {
      return { label: ws?.plan?.name || 'pro', color: undefined, sx: { bgcolor: '#FB923C', color: '#111827' } }; // orange
    }

    return { label: baseLabel, color: 'default' };
  }

  // Retourne 'freeOver' si free dépasse le prorata 1M ou la projection > 1M,
  // 'paidOver' si payant dépasse le prorata max_views ou la projection > max_views, sinon 'none'
  getProjectedOveruseStatus(entry) {
    const loads = Number(entry?.nbLoad || 0);
    const monthNum = parseInt(entry?._id?.load_month || 0, 10);
    const yearNum = parseInt(entry?._id?.load_year || 0, 10);
    if (!monthNum || !yearNum) return 'none';

    const today = new Date();
    const isCurrentMonth = (today.getFullYear() === yearNum) && ((today.getMonth() + 1) === monthNum);
    const daysInMonth = new Date(yearNum, monthNum, 0).getDate();
    const daysElapsed = isCurrentMonth ? today.getDate() : daysInMonth;

    const projected = daysElapsed > 0 ? Math.round((loads / daysElapsed) * daysInMonth) : loads;

    const rights = (entry?._id?.website?.rights || '').toLowerCase();
    const maxViews = Number(entry?._id?.website?.plan?.meta?.max_views);

    // Free: limite 1 000 000/mois
    if (rights === 'free') {
      const freeCap = 1000000;
      const freeCapProrata = Math.floor((freeCap * daysElapsed) / daysInMonth);
      if (loads > freeCapProrata || projected > freeCap) {
        return 'freeOver';
      }
      return 'none';
    }

    // Payant: limite = plan.meta.max_views (si définie)
    if (Number.isFinite(maxViews) && maxViews > 0) {
      const paidCapProrata = Math.floor((maxViews * daysElapsed) / daysInMonth);
      if (loads > paidCapProrata || projected > maxViews) {
        return 'paidOver';
      }
    }

    return 'none';
  }

  renderPageSizeSelect(id) {
    const { pageSize } = this.state;
    return (
      <FormControl size="small" sx={{ minWidth: 110 }}>
        <InputLabel id={`${id}-label`}>Par page</InputLabel>
        <Select
          labelId={`${id}-label`}
          label="Par page"
          value={pageSize}
          inputProps={{ 'data-testid': id }}
          onChange={(e) => this.setState({ pageSize: Number(e.target.value) }, () => this.fetchPage(1))}
        >
          {PAGE_SIZES.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
        </Select>
      </FormControl>
    );
  }

  render() {
    const { hits, page, total_elem, comments, loads, month, year, pageSize, paidOnly, query, isLoading, error, filtersIgnored: ignored } = this.state;
    const pageCount = pageCountFor(total_elem, pageSize);
    const nf = new Intl.NumberFormat('fr-FR');
    const state = screenState({ isLoading, error, hits });

    return (
      <Box sx={{ p: 2 }} data-testid="statviews-screen" data-state={state}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6">Statistiques de vues</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} flexWrap="wrap">
            <Typography variant="h5" color="primary" sx={{ lineHeight: 1 }}>
              Loads: {nf.format(parseInt(loads || 0, 10))}
            </Typography>
            <Chip label={`Total: ${nf.format(parseInt(total_elem || 0, 10))}`} data-testid="statviews-total" />
            <Chip color="secondary" label={`Comments: ${nf.format(parseInt(comments || 0, 10))}`} />
          </Stack>
        </Stack>

        <Box component="form" onSubmit={this.onFilterSubmit} sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="month-label">Month</InputLabel>
            <Select
              labelId="month-label"
              label="Month"
              value={month}
              inputProps={{ 'data-testid': 'statviews-month' }}
              onChange={(e) => this.setState({ month: e.target.value })}
            >
              {[...Array(12)].map((_, i) => {
                const v = String(i + 1);
                const label = (i + 1).toString().padStart(2, '0');
                return <MenuItem key={v} value={v}>{label}</MenuItem>;
              })}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="year-label">Year</InputLabel>
            <Select
              labelId="year-label"
              label="Year"
              value={year}
              inputProps={{ 'data-testid': 'statviews-year' }}
              onChange={(e) => this.setState({ year: e.target.value })}
            >
              {yearOptions().map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Site (id, nom ou clé)"
            value={query}
            inputProps={{ 'data-testid': 'statviews-query' }}
            onChange={(e) => this.setState({ query: e.target.value })}
            sx={{ minWidth: { xs: '100%', sm: 260 } }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={paidOnly}
                inputProps={{ 'data-testid': 'statviews-paid-only' }}
                onChange={(e) => this.setState({ paidOnly: e.target.checked }, () => this.fetchPage(1))}
              />
            }
            label="Plans payants uniquement"
          />

          {this.renderPageSizeSelect('statviews-page-size')}

          <Button type="submit" variant="contained" size="small" disabled={isLoading}>Search</Button>
        </Box>

        {error === 'session_expired' && (
          <Alert severity="warning" sx={{ mb: 2 }}>Session expirée : reconnectez-vous pour afficher les statistiques.</Alert>
        )}
        {error && error !== 'session_expired' && (
          <Alert severity="error" sx={{ mb: 2 }}>Chargement impossible ({error}).</Alert>
        )}
        {ignored && !error && (
          <Alert severity="warning" sx={{ mb: 2 }} data-testid="statviews-filters-ignored">
            Le serveur n'a pas appliqué les filtres demandés (API antérieure au correctif) : la liste et les totaux couvrent tous les sites.
          </Alert>
        )}

        <Paper variant="outlined">
          <TableContainer sx={{ maxHeight: '70vh' }}>
            <Table
              size="medium"
              stickyHeader
              sx={{
                tableLayout: 'fixed',
                '& tbody tr:nth-of-type(odd)': { bgcolor: 'action.hover' }
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Website</TableCell>
                  <TableCell align="center">Locked</TableCell>
                  <TableCell align="right">Loads</TableCell>
                  <TableCell align="right">HasRead</TableCell>
                  <TableCell align="right">Comments</TableCell>
                  <TableCell align="right">Taux Com.</TableCell>
                  <TableCell>Month/Year</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Languages</TableCell>
                </TableRow>
              </TableHead>
              <TableBody data-testid="statviews-rows" data-count={hits.length}>
                {hits.map((item) => {
                  const loadsVal = parseInt(item.nbLoad || 0, 10);
                  const hasRead = parseInt(item.hasReadComments || 0, 10);
                  const nbComments = parseInt(item.nbComments || 0, 10);
                  const tauxComment = loadsVal > 0 ? ((nbComments / loadsVal) * 100).toFixed(2) : '0.00';
                  const tauxHasRead = loadsVal > 0 ? parseInt((hasRead / loadsVal) * 100, 10) : 0;
                  const planChip = this.getPlanChipProps(item._id?.website || {});
                  const status = this.getProjectedOveruseStatus(item);
                  const rowBg = status === 'freeOver' ? '#FEE2E2' : (status === 'paidOver' ? '#FFEDD5' : undefined);

                  return (
                    <TableRow
                      key={item._id?.website?._id}
                      hover
                      data-website-id={item._id?.website?._id}
                      sx={rowBg ? { bgcolor: rowBg, '&:nth-of-type(odd)': { bgcolor: rowBg } } : undefined}
                      style={rowBg ? { backgroundColor: rowBg } : undefined}
                    >
                      <TableCell sx={{ width: { xs: 160, md: 240 } }}>
                        <Tooltip title={item._id?.website?._id || ''}>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
                            component={RouterLink}
                            to={`/super-admin/website/?website_id=${item._id?.website?._id}`}
                          >
                            {item._id?.website?._id}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ width: { xs: 240, md: 480 } }}>
                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item._id?.website?.name}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ width: 100 }}>
                        {(() => {
                          const isLocked = (typeof item?.locked !== 'undefined') ? Boolean(item.locked) : Boolean(item?._id?.website?.locked);
                          return (
                            <Tooltip title={isLocked ? 'Locked' : 'Unlocked'}>
                              <Chip size="small" icon={isLocked ? <LockIcon sx={{ fontSize: 16 }} /> : <LockOpenIcon sx={{ fontSize: 16 }} />} label={String(isLocked)} color={isLocked ? 'warning' : 'default'} />
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      <TableCell align="right"><Typography variant="body1" sx={{ fontWeight: 700 }} color="primary">{nf.format(loadsVal)}</Typography></TableCell>
                      <TableCell align="right">{nf.format(hasRead)} <Typography component="span" variant="caption">({tauxHasRead}%)</Typography></TableCell>
                      <TableCell align="right">{nf.format(nbComments)} <Typography component="span" variant="caption">({hasRead > 0 ? ((nbComments / hasRead) * 100).toFixed(2) : '0.00'}%)</Typography></TableCell>
                      <TableCell align="right">{tauxComment}%</TableCell>
                      <TableCell>{`${String(item._id?.load_month || '').padStart(2,'0')}/${item._id?.load_year || ''}`}</TableCell>
                      <TableCell>
                        <Chip size="small" label={planChip.label} color={planChip.color} sx={planChip.sx} />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {(item._id?.website?.languages || []).slice(0, 2).map((lng) => (
                            <Chip key={lng} size="small" label={lng} />
                          ))}
                          {((item._id?.website?.languages || []).length > 2) && (
                            <Chip size="small" label={`+${(item._id?.website?.languages || []).length - 2}`} />
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {hits.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {isLoading ? 'Chargement…' : `Aucun site avec des chargements en ${String(month).padStart(2, '0')}/${year}${paidOnly ? ' (plans payants)' : ''}${query.trim() ? ` pour « ${query.trim()} »` : ''}.`}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" data-testid="statviews-page-label">Page {page} / {pageCount}</Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              {this.renderPageSizeSelect('statviews-page-size-bottom')}
              <Pagination
                page={page}
                count={pageCount}
                onChange={this.onPageChange}
                size="small"
                color="primary"
                showFirstButton
                showLastButton
                siblingCount={0}
                boundaryCount={1}
              />
            </Stack>
          </Box>
        </Paper>
      </Box>
    );
  }
}

export default StatViews;
